import { Link, Navigate } from "react-router-dom";
import {
  Lightbulb,
  FileText,
  Mic,
  Image as ImageIcon,
  Play,
  Clock,
  BarChart3,
  DollarSign,
  Rocket,
  Check,
  ArrowRight,
  Factory,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// The mockup image (uploaded by the user) doubles as the hero art.
// We only need the right-hand side (the robotic arm on the
// conveyor belt) — the left side has design text that we replace
// with real HTML, so we clip to the right half via object-position.
const HERO_ART =
  "https://customer-assets.emergentagent.com/job_channel-analyzer-33/artifacts/tfkxo8by_New%20Factory%20UI.png";

const HOW_IT_WORKS = [
  { icon: Lightbulb, title: "Idea", body: "AI finds proven ideas that get views." },
  { icon: FileText, title: "Script", body: "AI writes engaging, high-retention scripts." },
  { icon: Mic, title: "Voiceover", body: "Natural AI voiceover in any style." },
  { icon: ImageIcon, title: "Visuals", body: "AI creates stunning visuals and edits." },
  { icon: Play, title: "Publish", body: "Auto-publish to your YouTube channel." },
];

const BENEFITS = [
  {
    icon: Clock,
    title: "Save Hours",
    body: "Automate the entire process from idea to publish.",
  },
  {
    icon: BarChart3,
    title: "Grow Faster",
    body: "Consistent, high-quality videos that grow your channel.",
  },
  {
    icon: DollarSign,
    title: "Lower Costs",
    body: "Cut editing and outsourcing costs to almost zero.",
  },
  {
    icon: Rocket,
    title: "Scale Effortlessly",
    body: "Create more videos, in more niches, effortlessly.",
  },
];

const NAV_ITEMS = [
  { label: "Features", href: "#features" },
  { label: "How It Works", href: "#how-it-works" },
  { label: "Pricing", href: "#pricing" },
  { label: "Examples", href: "#examples" },
  { label: "Blog", href: "#blog" },
];

function Logo() {
  return (
    <Link
      to="/"
      data-testid="landing-logo"
      className="inline-flex items-center gap-2 group"
    >
      <span
        aria-hidden
        className="w-9 h-9 rounded-md bg-yellow-400 grid place-items-center shadow-[0_0_24px_-8px_rgba(250,204,21,0.8)] group-hover:shadow-[0_0_28px_-4px_rgba(250,204,21,0.9)] transition-shadow"
      >
        <Play className="w-4 h-4 text-black fill-black -ml-0.5" strokeWidth={0} />
      </span>
      <span className="font-display font-black text-xl text-white tracking-tight">
        YT<span className="text-yellow-400">Maker</span>.AI
      </span>
    </Link>
  );
}

function Nav() {
  return (
    <header
      data-testid="landing-nav"
      className="sticky top-0 z-30 backdrop-blur-md bg-black/70 border-b border-zinc-900/80"
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between gap-6">
        <Logo />
        <nav
          data-testid="landing-nav-links"
          className="hidden md:flex items-center gap-8"
        >
          {NAV_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              className="text-sm text-zinc-400 hover:text-yellow-300 transition-colors"
            >
              {item.label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="landing-login-link"
            className="hidden sm:inline-flex text-sm text-zinc-300 hover:text-yellow-300 transition-colors px-3 py-2"
          >
            Log in
          </Link>
          <Link
            to="/login"
            data-testid="landing-nav-cta"
            className="inline-flex items-center gap-1.5 bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-sm px-5 py-2.5 rounded-md transition-colors shadow-[0_0_20px_-8px_rgba(250,204,21,0.9)]"
          >
            Get Started
          </Link>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section
      data-testid="landing-hero"
      className="relative overflow-hidden"
    >
      {/* Ambient yellow glow behind the robotic arm */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 w-[65%] opacity-40"
        style={{
          background:
            "radial-gradient(60% 55% at 70% 45%, rgba(250,204,21,0.14), transparent 70%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8 lg:pt-24 lg:pb-16 grid lg:grid-cols-12 gap-10 items-center">
        {/* LEFT — copy + CTAs */}
        <div className="lg:col-span-6 space-y-8 relative z-10">
          <span
            data-testid="landing-hero-eyebrow"
            className="inline-flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-yellow-400 border border-yellow-400/40 bg-yellow-400/5 rounded-full px-4 py-2"
          >
            Automated. Faceless. Scalable.
          </span>

          <h1
            data-testid="landing-hero-title"
            className="font-display font-black tracking-tight text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95]"
          >
            Your YouTube
            <br />
            <span className="text-yellow-400">content factory.</span>
          </h1>

          <p
            data-testid="landing-hero-sub"
            className="text-lg text-zinc-400 max-w-xl leading-relaxed"
          >
            YTMaker.AI automates every step of creating and publishing
            faceless YouTube videos — from proven ideas to polished
            uploads.
          </p>

          <div className="flex flex-wrap items-center gap-4 pt-2">
            <Link
              to="/login"
              data-testid="landing-hero-primary-cta"
              className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-base px-6 py-3.5 rounded-lg transition-colors shadow-[0_0_36px_-10px_rgba(250,204,21,0.95)] hover:shadow-[0_0_44px_-6px_rgba(250,204,21,1)]"
            >
              Start Your Free Trial
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#how-it-works"
              data-testid="landing-hero-secondary-cta"
              className="inline-flex items-center gap-2 border border-zinc-700 hover:border-yellow-400/60 text-zinc-200 hover:text-yellow-300 text-base px-6 py-3.5 rounded-lg transition-colors"
            >
              <span className="w-7 h-7 rounded-full border border-current grid place-items-center">
                <Play className="w-3 h-3 fill-current" strokeWidth={0} />
              </span>
              Watch how it works
            </a>
          </div>

          <ul
            data-testid="landing-hero-trust"
            className="flex flex-wrap gap-x-6 gap-y-2 pt-2 text-sm text-zinc-400"
          >
            {[
              "No credit card required",
              "Cancel anytime",
              "Trusted by creators",
            ].map((t) => (
              <li key={t} className="inline-flex items-center gap-1.5">
                <Check className="w-4 h-4 text-yellow-400" />
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* RIGHT — hero art (robotic arm + conveyor from the mockup).
            The source image is a full landing-page mockup (design
            reference from the user). We use background-image with
            an aggressive zoom + position to isolate ONLY the
            robotic-arm-on-conveyor scene from the top-right of
            the mockup. */}
        <div
          data-testid="landing-hero-art"
          role="img"
          aria-label="AI robotic arm assembling YouTube video components on a conveyor belt"
          className="lg:col-span-6 relative aspect-[4/3] lg:aspect-[5/4] rounded-2xl overflow-hidden bg-black"
          style={{
            backgroundImage: `url(${HERO_ART})`,
            backgroundRepeat: "no-repeat",
            // 275% zoom + anchor on the robotic arm at ~(76%, 32%)
            // of the mockup → the arm & conveyor fill the frame,
            // and the mockup's own UI text is cropped off-canvas.
            backgroundSize: "340% auto",
            backgroundPosition: "76% 22%",
          }}
        >
          {/* Left-edge fade so the crop blends into the page bg */}
          <div
            aria-hidden
            className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-black to-transparent"
          />
          <div
            aria-hidden
            className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 to-transparent"
          />
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="landing-how-it-works"
      className="relative py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center space-y-3">
          <div className="font-mono text-[11px] font-bold uppercase tracking-[0.35em] text-yellow-400">
            How It Works
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight">
            Idea to upload, on autopilot
          </h2>
        </div>

        <ol
          data-testid="landing-how-it-works-steps"
          className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-3 relative"
        >
          {/* Dashed connector line (desktop only) */}
          <div
            aria-hidden
            className="hidden lg:block absolute top-[68px] left-[10%] right-[10%] border-t-2 border-dashed border-yellow-400/25"
          />

          {HOW_IT_WORKS.map((step, i) => {
            const Icon = step.icon;
            return (
              <li
                key={step.title}
                data-testid={`landing-step-${i + 1}`}
                className="relative bg-zinc-950/60 border border-zinc-900 hover:border-yellow-400/40 rounded-2xl p-6 pt-10 transition-colors group"
              >
                {/* Numbered circle */}
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div className="w-9 h-9 rounded-full bg-yellow-400 text-black font-black text-sm grid place-items-center shadow-[0_0_20px_-4px_rgba(250,204,21,0.8)]">
                    {i + 1}
                  </div>
                </div>
                <div className="flex flex-col items-center text-center gap-3">
                  <div className="w-14 h-14 rounded-xl bg-black border border-zinc-800 grid place-items-center group-hover:border-yellow-400/40 transition-colors">
                    <Icon className="w-7 h-7 text-yellow-400" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display font-bold text-white text-lg">
                    {step.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {step.body}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

function Benefits() {
  return (
    <section
      id="features"
      data-testid="landing-benefits"
      className="py-20 lg:py-28"
    >
      <div className="max-w-7xl mx-auto px-6">
        <h2 className="text-center font-display text-3xl sm:text-4xl font-black text-white tracking-tight">
          Built to <span className="text-yellow-400">scale</span> your faceless channel
        </h2>

        <ul className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <li
                key={b.title}
                data-testid={`landing-benefit-${i + 1}`}
                className="bg-zinc-950/60 border border-zinc-900 hover:border-yellow-400/40 rounded-2xl p-6 space-y-4 transition-colors"
              >
                <div className="w-11 h-11 rounded-lg bg-yellow-400/10 border border-yellow-400/20 grid place-items-center">
                  <Icon className="w-5 h-5 text-yellow-400" strokeWidth={1.75} />
                </div>
                <div className="space-y-1.5">
                  <h3 className="font-display font-bold text-white text-lg">
                    {b.title}
                  </h3>
                  <p className="text-sm text-zinc-400 leading-relaxed">
                    {b.body}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

function EmpireCTA() {
  return (
    <section
      data-testid="landing-empire-cta"
      className="pb-24 lg:pb-32"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="relative overflow-hidden rounded-3xl border border-yellow-400/30 bg-gradient-to-br from-yellow-400/[0.08] via-black to-black p-8 lg:p-12">
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-16 -left-8 opacity-20"
          >
            <Factory className="w-56 h-56 text-yellow-400" strokeWidth={0.8} />
          </div>

          <div className="relative grid lg:grid-cols-2 items-center gap-8">
            <div className="lg:pl-40 space-y-3">
              <h2 className="font-display text-3xl sm:text-4xl font-black text-white tracking-tight">
                Ready to build your{" "}
                <span className="text-yellow-400">YouTube empire?</span>
              </h2>
              <p className="text-zinc-400 leading-relaxed max-w-lg">
                Join thousands of creators automating their way to more
                views and revenue.
              </p>
            </div>
            <div className="flex flex-col items-start lg:items-end gap-3">
              <Link
                to="/login"
                data-testid="landing-empire-cta-btn"
                className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-base px-6 py-3.5 rounded-lg transition-colors shadow-[0_0_36px_-10px_rgba(250,204,21,0.95)] hover:shadow-[0_0_44px_-6px_rgba(250,204,21,1)]"
              >
                Start Your Free Trial
                <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="inline-flex items-center gap-1.5 text-sm text-zinc-400">
                <Check className="w-4 h-4 text-yellow-400" />
                No credit card required
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer
      data-testid="landing-footer"
      className="border-t border-zinc-900 py-10"
    >
      <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
        <Logo />
        <div className="text-xs text-zinc-500 font-mono tracking-wide">
          &copy; {new Date().getFullYear()} YTMaker.AI · Automated. Faceless. Scalable.
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  const { user, loading } = useAuth();
  // Signed-in users skip the marketing page and land in their
  // journey dashboard so /ytmaker.ai/ still feels like "home" to
  // returning customers.
  if (!loading && user) {
    return <Navigate to="/journey" replace />;
  }
  return (
    <div
      data-testid="landing-page"
      className="min-h-screen w-full bg-black text-zinc-100 selection:bg-yellow-400 selection:text-black"
    >
      <Nav />
      <Hero />
      <HowItWorks />
      <Benefits />
      <EmpireCTA />
      <Footer />
    </div>
  );
}
